/**
 * Pages Router global 404 (also served at `/errors/404/`). Main error copy + search, then a “Trending Stories” module below when `mostPopular` data is available.
 */
import { useEffect } from 'react';
import { mockHeadData } from 'utils/metaDataUtils/headData';
import type { HeaderNav } from 'interfaces/navigation/Header';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { setPianoConfig } from 'services/Piano';
import Error404 from 'components/Error404';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import type { PageViewDataLayerObject } from 'services/Gtm/entities';
import mockPageData from 'src/main/lib/mockData/page-data';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import getFooter from 'api/Footer/getFooter';
import getMenu from 'api/Header/getMenu';
import getMostPopularPosts from 'api/MostPopular/getMostPopular';
import { getGtmPageStatusEvent, pushToDataLayer } from 'services/Gtm/functions';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import { PianoExperiencePageType } from '@/app_lib/types/piano/Paywall';
import { mapAppArticlePreviewToPagesArticlePreview } from 'lib/api/MostPopular/mapAppArticlePreviewToPagesArticlePreview';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';

const Custom404 = ({ mostPopular }: { mostPopular?: ArticlePreview[] }) => {
  useEffect(() => {
    pushToDataLayer(getGtmPageStatusEvent(true, 404));
  }, []);

  return (
    <Error404
      heading='Oops! Page not found'
      link='homepage'
      mostPopular={mostPopular}
      msg='Our apologies. It may have expired or there could be a typo.
      Search for what you are looking for or find what you need on our'
    />
  );
};

export async function getStaticProps() {
  try {
    const menu: HeaderNav = await getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null).catch(
      () => ({
        domainLogo: null,
        leftMenu: [],
        rightMenu: [],
        subDomain: 'own',
        topicNavigation: [],
      }),
    );

    const footer = await getFooter().catch(() => ({
      footerDisclaimer: '',
      footerNav: [],
      socialList: [],
    }));

    let mostPopular = await getMostPopularPosts().catch(() => [] as ArticlePreview[]);

    if (mostPopular.length === 0) {
      const { fetchTrendingArticlesFor404 } = await import('@/app_lib/utils/pages/loadTrendingFor404');
      const { mostPopularToArticlePreview } = await import('@/app_lib/utils/pages/mostPopularToArticlePreview');
      const trendingRaw = await fetchTrendingArticlesFor404();
      const appPreviews = trendingRaw
        .map(mostPopularToArticlePreview)
        .filter((p): p is NonNullable<typeof p> => p != null);
      mostPopular = appPreviews.map(mapAppArticlePreviewToPagesArticlePreview);
    }

    const dataLayer: PageViewDataLayerObject = setPageGtmConfig(mockPageData);
    const arcSpanPageConfig: ArcSpanPageConfig = setarcSpanPageConfig(mockPageData);
    const pianoPageConfig: PianoPageConfig = setPianoConfig({
      pageType: PianoExperiencePageType.Default,
      pageData: {},
    });

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        headData: mockHeadData,
        headerNav: menu,
        mostPopular,
        pianoPageConfig,
      },
      revalidate: 600,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
}

export default Custom404;
